import * as React from "react";
import { IconButton, Typography, Link } from "@mui/material";
import Grid from '@mui/material/Grid2';
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";
import dayjs from "dayjs";
import ComplianceScorecardAssistanceModal from "./ComplianceScorecardAssistanceModal";
import _ from "lodash";
import { getPerCapitaDeadline } from "utils/dateUtils";

interface IAffiliateCompliance {
  perCapitaNationalCompliantDate: string;
  perCapitaStateCompliantDate: string;
  auditCompliantDate: string;
  officerInformationCompliantDate: string;
  executiveBoardInformationCompliantDate: string;
  membershipCompliantDate: string;
  constitutionCompliantDate: string;
  duesResultId: number;
  stateDuesResultId: number;
  auditResultId: number;
  officersResultId: number;
  membershipResultId: number;
  constitutionResultId: number;
  execBoardResultId: number;
  isCertified: boolean;
  isCertifiedDate: string;
  [key: string]: unknown;
}

export interface ISelectedAffiliate {
  affiliateCompliance?: IAffiliateCompliance;
  [key: string]: unknown;
}

interface IComplianceScorecardProps {
  selectedAffiliate: ISelectedAffiliate;
}

const ComplianceScorecard: React.FC<IComplianceScorecardProps> = (props) => {
  const affiliateCompliance = props.selectedAffiliate?.affiliateCompliance;

  const renderProperIconForGivenValue = (complianceId: number) => {
    switch (complianceId) {
      case 1:
        return <CheckIcon className="in-compliance" />;
      case 2:
        return <ClearIcon className="not-in-compliance" />;
      case 3:
        return (
          <span
            style={{ color: "#ECEC19", fontSize: "1.25em", fontWeight: 700 }}
          >
            ?
          </span>
        );
      case 5:
        return (
          <span style={{ color: "black", fontSize: "1.25em", fontWeight: 700 }}>
            P
          </span>
        );
      default:
        return <span>N/A</span>;
    }
  };

  const renderCorrectComplianceAttribute = (
    complianceTitle: string,
    complianceWarning: string, 
    complianceId: number,
    date: string
  ) => {
    return (
      <div style={{width:"100%", display:"flex", flexDirection:"column"}}>      
      <div>
      <Grid container className="compliance-scorecard-body">
        <Grid size={{ xs: 4 }} className="p-2">
          <span className="compliance-section-title">{complianceTitle}</span>
        </Grid>
        <Grid size={{ xs: 2 }} className="text-center p-2">
          <span>{renderProperIconForGivenValue(complianceId)}</span>
        </Grid>
        <Grid size={{ xs: 4 }} className="text-center compliance-section-date p-2">
          <span>{date ? dayjs(date).format("MM/DD/YYYY") : "N/A"}</span>
        </Grid>
        <Grid size={{ xs: 2 }} className="text-center compliance-section-date">
          <IconButton
            aria-label="open-help-modal"
            onClick={() => {
              setModalTitle(complianceTitle);
              setComplianceScorecardInfoModalOpen(true);
            }}
          >
            <InfoIcon />
          </IconButton>
        </Grid>
        </Grid>
      </div>
      <div style={{display:"flex", justifyContent:"start", paddingLeft:"18px"}}>
        <div style={{marginTop:"-20px"}}>
          <i style={{fontSize:"12px"}}>{complianceWarning ?? ""}</i>
        </div>
      </div>

    </div>

    );
  };

  const checkToSeeIfAffiliateIsInCompliance = () => {
    const complianceDetails =
      (_.get(props, "selectedAffiliate.affiliateCompliance") || {}) as Record<string, unknown>;

    const complianceStatusKeys = Object.keys(complianceDetails).filter(
      (k) => k.indexOf("Compliant") >= 0 && k.indexOf("Date") == -1
    );

    let inCompliance = _.every(
      _.map(complianceStatusKeys, (key: string) => {
        return complianceDetails[key];
      }),
      Boolean
    );

    if (!inCompliance) {
      return (
        <Grid>
          <Grid size={{ xs: 12, md: 12 }} className="compliance-notification">
            <span>
              <PriorityHighOutlinedIcon className={"not-compliant-marker"} /> This
            affiliate is not in compliance{" "}
              <PriorityHighOutlinedIcon className={"not-compliant-marker"} />
            </span>
          </Grid>

          <Grid size={{ xs: 12, md: 12 }} className="compliance-notification">
            <span>
              Compliance Status will not prevent you from registering your delegates for convention. 
              Please complete your registrations and <Link className="compliance-scorecard-contact-link" href="mailto:AFTConvention@aft.org">Contact Us</Link> if you have questions.
          </span>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const [
    complianceScorecardInfoModalOpen,
    setComplianceScorecardInfoModalOpen,
  ] = React.useState(false);

  const [modalTitle, setModalTitle] = React.useState("");

  const renderCorrectContentForAssistanceModal = () => {
    switch (modalTitle) {
      case "Per Capita (Nat'l)":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                We show the last time you submitted National Per Capita was on{" "}
                {dayjs(
                  affiliateCompliance!
                    .perCapitaNationalCompliantDate
                ).format("MM/DD/YYYY")}
                .
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>ARTICLE X</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>Good Standing</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <span>
                Section 1. To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-1">
              <ol type="a">
                <li>
                  payment of per capita to the AFT on all members, with arrears
                  not to exceed two months, and to the applicable state
                  federation consistent with its constitution;
                </li>
                <li>
                  current in submission of the affiliate’s annual audit or
                  financial review in keeping with Sections 1 and 2 of Article
                  IX of the AFT bylaws;
                </li>
                <li>
                  current in submission to the AFT of names and addresses for
                  the principal executive and financial officers;
                </li>
                <li>
                  current in submission to the AFT of the names and addresses of
                  current members of the affiliate’s executive board;
                </li>
                <li>current in submission of a membership roster; and</li>
                <li>
                  current in submission of a copy of the affiliate’s up-to-date
                  constitution.
                </li>
              </ol>
            </Grid>
          </Grid>
        );
      case "Per Capita (State)":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                If you have questions regarding your compliance with your state
                federation per capita requirement, please contact your state
                federation directly.
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>ARTICLE X</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>Good Standing</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>
                Section 1. To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-1">
              <ol type="a">
                <li>
                  payment of per capita to the AFT on all members, with arrears
                  not to exceed two months, and to the applicable state
                  federation consistent with its constitution;
                </li>
                <li>
                  current in submission of the affiliate’s annual audit or
                  financial review in keeping with Sections 1 and 2 of Article
                  IX of the AFT bylaws;
                </li>
                <li>
                  current in submission to the AFT of names and addresses for
                  the principal executive and financial officers;
                </li>
                <li>
                  current in submission to the AFT of the names and addresses of
                  current members of the affiliate’s executive board;
                </li>
                <li>current in submission of a membership roster; and</li>
                <li>
                  current in submission of a copy of the affiliate’s up-to-date
                  constitution.
                </li>
              </ol>
            </Grid>
          </Grid>
        );
      case "Audit":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                We show the next Affiliate Audit Report due date to be&nbsp;
                {dayjs(
                affiliateCompliance!.auditCompliantDate
              ).format("MM/DD/YYYY")}
                . If you feel this date is in error, please send an email to
                auditreports@aft.org. A "P" under audit
                means pending; the audit has been received and it is awaiting
                review or additional materials.
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <span>
                Reaffirming the importance of financial accountability while
                acknowledging the practical limitations for some affiliates,
                delegates to the 2010 AFT convention adopted amendments to the
                AFT bylaws that require outside audits of affiliates with more
                than 2,500 members, outside audit or a financial review for
                affiliates with between 1,000 and 2,500 members, and either an
                outside audit or review, or an internal financial review for
                affiliates with fewer than 1,000 members. These changes are
                effective for fiscal years ending in 2011:
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              Article IX, Sections 1, 2 and 3
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2">
              Section 1. AFT affiliates with 2,500 or more members, excluding
              retirees, are required to submit to the AFT annually, and within
              six months of the close of the affiliate’s fiscal year, an audit
              of its financial statements—in accordance with generally accepted
              auditing standards—by an independent CPA whose firm that does not
              have responsibility for the affiliate’s regular bookkeeping or for
              preparation of its routine financial statements. The audit must be
              prepared in accordance with generally accepted accounting
              principles. Appended to the audit submission must be a letter
              signed by the affiliate’s principal officer and the auditor
              certifying that the audit has been presented at a specified
              regular or special meeting of the affiliate’s governing body and
              published and made available to the members.
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2">
              Section 2. AFT affiliates with 1,000 but fewer than 2,500 members,
              excluding retirees, are required to submit to the AFT annually,
              and within six months of the close of the affiliate’s fiscal year,
              an audit or a review of its financial statements – in accordance
              with generally accepted auditing standards – by an independent CPA
              who firm does not have responsibility for the affiliate’s regular
              bookkeeping or for preparation of its routine financial
              statements. The audit or the review must be prepared in accordance
              with generally accepted accounting principles. Appended to the
              audit or review submission must be a letter signed by the
              affiliate’s principal officer and the CPA certifying that the
              audit or the review has been presented at a specified regular or
              special meeting of the affiliate’s governing body and published
              and made available to the members. If financial irregularity or
              financial misfeasance is indicated, the three full-time AFT
              officers may require an affiliate to submit an audit, rather than
              a review. If an affiliate is notified that it must submit an
              audit, the requirement to do so shall remain in place until the
              three full-time AFT officers inform the affiliate that it is no
              longer required.
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2">
              Section 3. AFT affiliates with fewer than 1,000 members, excluding
              retirees, are required to submit to the AFT annually, and within
              six months of the close of the affiliate’s fiscal year, an audit
              of its financial statements by an independent CPA in accordance
              with generally accepted auditing standards or a financial review
              of the affiliate’s financial statements conducted by an internal
              audit committee in accordance with AFT guidelines for audit
              committees. Appended to the audit or financial review must be a
              signed certification by the affiliate’s principal officer that the
              current audit or financial review has been presented at a
              specified regular or special meeting of the affiliate’s governing
              body and published and made available to the members.
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2">
              Section 4. Where a required audit, independent CPA review or
              financial review has not been performed and received by the AFT
              within six months of the close of an affiliate’s fiscal year, the
              AFT president is authorized to have the applicable audit,
              independent CPA review or financial review, pursuant to Section 1,
              2 or 3 of this Article, conducted at the affiliate’s expense. Such
              an analysis will include an examination of the financial records
              and a reporting of the results to the membership of the affiliate
              and to the AFT executive council.
            </Grid>
          </Grid>
        );
      case "Officer Info":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                We show the last time you updated Officers Information was on{" "}
                {dayjs(
                  affiliateCompliance!
                    .officerInformationCompliantDate
                ).format("MM/DD/YYYY")}
                . If you feel this date is in error, please email aftconvention@aft.org.
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <div>Bylaw X:</div>
              <div>
                Section 1: To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </div>
              <span>
                Section 1(c): Current in submission to the AFT of names and
                addresses for the principal executive and financial officers;
              </span>
            </Grid>
          </Grid>
        );
      case "Exec Board Info":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                We show the last time you updated Exec Board Members Information
                was on{" "}
                {dayjs(
                  affiliateCompliance!
                    .executiveBoardInformationCompliantDate
                ).format("MM/DD/YYYY")}
                . If you feel this date is in error, please email aftconvention@aft.org.
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <div>Bylaw X:</div>
              <div>
                Section 1. To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </div>
              <div>
                Section 1(d): current in submission to the AFT of the names and
                addresses of current members of the affiliate’s executive board;
              </div>
            </Grid>
          </Grid>
        );

      case "Membership":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <span>
                We show the last time you updated the Membership Information was
                on{" "}
                {dayjs(
                  affiliateCompliance!
                    .membershipCompliantDate
                ).format("MM/DD/YYYY")}
                . If you feel this date is in error, please email aftconvention@aft.org.
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <div>Bylaw X:</div>
              <div>
                Section 1. To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </div>
              <div>
                Section 1(e): current in submission of a membership roster;
              </div>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-3">
              <p>
                Article IX: Effective September 1, 1990, the treasurer of each
                affiliated local will fill out and forward to the national
                office, on or before the 15th day of each month, the report of
                active members in good standing and retiree members who have
                retired since the last reporting period. Members in good
                standing of chartered retiree organizations will be reported to
                the national office on or before the 15th day of each month
                together with mailing addresses, on the first day of that month,
                together with all taxes and assessments due the American
                Federation of Teachers. Locals that have once submitted names
                and addresses of the members will revise and correct the
                membership list with each per-capita payment. The report will be
                subject to an audit by the secretary-treasurer’s office.
              </p>
            </Grid>
          </Grid>
        );
      case "Constitution":
        return (
          <Grid size={{ xs: 12, md: 12 }}>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2 pb-2 font-weight-bold">
              <div>
                The date shown{" "}
                {dayjs(
                  affiliateCompliance!
                    .constitutionCompliantDate
                ).format("MM/DD/YYYY")}{" "}
                indicates:
              </div>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <p>
                the date on the affiliate Constitution; or{" "}
                <div>
                  if there was no date on the Constitution when submitted the
                  date shown is the date the Constitution was received.
                </div>{" "}
                <div>
                  If you’d like to communicate with someone regarding your
                  affiliate’s Constitution, please email
                  sectreas@aft.org.
                </div>
              </p>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>ARTICLE X</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>Good Standing</span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <span>
                Section 1. To be in good standing in the AFT, affiliates must
                meet the following requirements:
              </span>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-1">
              <ol type="a">
                <li>
                  payment of per capita to the AFT on all members, with arrears
                  not to exceed two months, and to the applicable state
                  federation consistent with its constitution;
                </li>
                <li>
                  current in submission of the affiliate’s annual audit or
                  financial review in keeping with Sections 1 and 2 of Article
                  IX of the AFT bylaws;
                </li>
                <li>
                  current in submission to the AFT of names and addresses for
                  the principal Executive and financial officers;
                </li>
                <li>
                  current in submission to the AFT of the names and addresses of
                  current members of the affiliate’s executive board;
                </li>
                <li>current in submission of a membership roster; and</li>
                <li>
                  Current in submission of a copy of the affiliate’s up-to-date
                  constitution
                </li>
              </ol>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (!affiliateCompliance) {
    return <Grid container size={{ xs: 12 }}><span>Compliance data not available</span></Grid>;
  }

  return (
    <Grid container size={{ xs: 12 }}>
      <Grid container size={{ xs: 12 }}>
        {props.selectedAffiliate && checkToSeeIfAffiliateIsInCompliance()}
      </Grid>
      <Grid container size={{ xs: 12 }} className={"pt-3 pb-3"}>
        <Grid size={{ xs: 12 }} className="user-info-head">
          <span>Compliance Scorecard</span>
        </Grid>
        {renderCorrectComplianceAttribute(
          "Per Capita (Nat'l)",
          `*Per Capita will show with an X until per capita is received through May by ${getPerCapitaDeadline()}`,
          affiliateCompliance.duesResultId,
          affiliateCompliance
            .perCapitaNationalCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Per Capita (State)",   
          "",
          affiliateCompliance.stateDuesResultId,
          affiliateCompliance
            .perCapitaStateCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Audit",
           "",
          affiliateCompliance.auditResultId,
          affiliateCompliance.auditCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Officer Info", 
          "",
          affiliateCompliance.officersResultId,
          affiliateCompliance
            .officerInformationCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Exec Board Info", 
          "",
          affiliateCompliance.execBoardResultId,
          affiliateCompliance
            .executiveBoardInformationCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Membership",       
          "",
          affiliateCompliance.membershipResultId,
          affiliateCompliance.membershipCompliantDate
        )}
        {renderCorrectComplianceAttribute(
          "Constitution",
          "",
          affiliateCompliance.constitutionResultId,
          affiliateCompliance.constitutionCompliantDate
        )}
      <Grid container className="compliance-scorecard-body">
        <Grid size={{ xs: 4 }} className="p-2">
          <span className="compliance-section-title">{"Certified"}</span>
        </Grid>
        <Grid size={{ xs: 2 }} className="text-center p-2">
          <span>{renderProperIconForGivenValue(affiliateCompliance.isCertified  ? 1 : 2)}</span>
        </Grid>
        <Grid size={{ xs: 4 }} className="text-center compliance-section-date p-2">

          <span>{affiliateCompliance.isCertifiedDate  ?  dayjs(affiliateCompliance.isCertifiedDate).format("MM/DD/YYYY "): "N/A"}</span>
        </Grid>
        <Grid size={{ xs: 2 }} className="text-center compliance-section-date">
          <IconButton
            aria-label="open-help-modal"
            onClick={() => {
              setModalTitle("Certified");
              setComplianceScorecardInfoModalOpen(true);
            }}
          >
            <InfoIcon />
          </IconButton>
        </Grid>
      </Grid>
      </Grid>
      <Grid size={{ xs: 12, md: 12 }} className={"score-card-legend p-3"}>
        <Grid size={{ xs: 12, md: 12 }}>
          {renderProperIconForGivenValue(1)}
          <span>In Compliance</span>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          {renderProperIconForGivenValue(2)}
          <span>Not in Compliance</span>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }} className="pl-1">
          {renderProperIconForGivenValue(3)}
          <span className="pl-2">Missing Data</span>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }} className="pending-wrapper pl-1">
          {renderProperIconForGivenValue(5)}
          <span className=" pl-2">Audit Pending Review</span>
        </Grid>
      </Grid>
      <ComplianceScorecardAssistanceModal
        modalOpen={complianceScorecardInfoModalOpen}
        modalTitle={`${modalTitle}`}
        onClose={() => setComplianceScorecardInfoModalOpen(false)}
        content={
          <Grid container size={{ xs: 12 }}>
            {renderCorrectContentForAssistanceModal()}{" "}
            <Grid size={{ xs: 12 }} className="pt-2">
              The local president must certify that delegates have been nominated and elected in accordance with the provisions of our local constitution, the AFT constitution and the Landrum-Griffin Act using the form in the Delegate Certification page via the menu on the left. Contact aftconvention@aft.org if you have questions. 
            </Grid>
          </Grid>
        }
      />
    </Grid>
  );
};

export default ComplianceScorecard;
